import { ChipToggleGroup, ChipToggleOption } from '@app/components/chip-toggle-group';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useShallow } from 'zustand/react/shallow';
import { QUALITY_ICONS, QUALITY_STATUS_COLORS } from '../consts';
import { useReportViewStore } from '../store/view.store';
import {
  QUALITY_STATION_STATUS_FILTER_DISCONTINUITY,
  QUALITY_STATION_STATUS_FILTER_NO_INCIDENTS,
  QUALITY_STATION_STATUS_FILTER_POOR_QUALITY,
  QUALITY_STATION_STATUS_FILTER_SILENT,
  StationStatusFilterValue,
} from './filter-quality-stations-by-status';

const messages = defineMessages({
  filterByIncidentsLabel: {
    id: 'logger.reports.streamQuality.stationStatusFilter.filterByIncidentsLabel',
    defaultMessage: 'Filtrar por incidentes: ',
  },
  noIncidents: {
    id: 'logger.reports.streamQuality.stationStatusFilter.noIncidents',
    defaultMessage: 'Sin incidentes',
  },
  poorQuality: {
    id: 'logger.reports.streamQuality.stationStatusFilter.poorQuality',
    defaultMessage: 'Calidad deficiente',
  },
  silent: {
    id: 'logger.reports.streamQuality.stationStatusFilter.silent',
    defaultMessage: 'Silencio',
  },
  discontinuity: {
    id: 'logger.reports.streamQuality.stationStatusFilter.discontinuity',
    defaultMessage: 'Discontinuidad',
  },
});

/**
 * Multi-select chip row: filter timeline and issues table by station incident categories (OR).
 */
export function StationStatusFilter() {
  const { $t } = useIntl();
  const value = useReportViewStore(useShallow((state) => state.stationStatusFilter));
  const setStationStatusFilter = useReportViewStore((state) => state.setStationStatusFilter);

  const options: ChipToggleOption[] = useMemo(
    () => [
      {
        value: QUALITY_STATION_STATUS_FILTER_NO_INCIDENTS,
        label: $t(messages.noIncidents),
        chipColor: QUALITY_STATUS_COLORS.online,
        icon: <QUALITY_ICONS.healthScore fontSize="small" />,
        filledLabelColor: '#fff',
      },
      {
        value: QUALITY_STATION_STATUS_FILTER_POOR_QUALITY,
        label: $t(messages.poorQuality),
        chipColor: QUALITY_STATUS_COLORS.issuePoorQuality,
        icon: <QUALITY_ICONS.audioPoorQuality fontSize="small" />,
        filledLabelColor: '#fff',
      },
      {
        value: QUALITY_STATION_STATUS_FILTER_SILENT,
        label: $t(messages.silent),
        chipColor: QUALITY_STATUS_COLORS.issueSilent,
        icon: <QUALITY_ICONS.audioSilent fontSize="small" />,
        filledLabelColor: '#fff',
      },
      {
        value: QUALITY_STATION_STATUS_FILTER_DISCONTINUITY,
        label: $t(messages.discontinuity),
        chipColor: QUALITY_STATUS_COLORS.offline,
        icon: <QUALITY_ICONS.gap fontSize="small" />,
        filledLabelColor: '#fff',
      },
    ],
    [$t],
  );

  return (
    <Box
      sx={{
        mb: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1,
        rowGap: 0.5,
      }}
    >
      <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {$t(messages.filterByIncidentsLabel)}
      </Typography>
      <ChipToggleGroup
        multiple
        options={options}
        value={value}
        onChange={(next) => setStationStatusFilter(next as StationStatusFilterValue[])}
        chipSize="small"
      />
    </Box>
  );
}
